// ════════════════════════════════════════════════════════════════════════════
// ✅ pages/PublicEmergencies.js — PUBLIC SOS Management Page
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import "./css/PublicEmergencies.css";
import { getAllPublicSOS, resolvePublicSOS } from "../api/publicSosApi";
import { socket } from "../socket";
import AccessControl from "../components/AccessControl";
import { getPassengerFakeHistory } from "../api/publicSosApi";
import { markPublicSOSAsFake } from "../api/publicSosApi";
import Loader from "../components/Loader";

const REASON_EMOJI = {
  Accident: "💥",
  "Medical emergency": "🏥",
  "Robbery / Theft": "🦹",
  Harassment: "🚫",
  "Natural disaster": "🌊",
  "Fire emergency": "🔥",
  Other: "❗",
};

const STATUS_COLORS = {
  active: { bg: "#fff1f0", text: "#cf1322", border: "#ffa39e" },
  responded: { bg: "#fff7e6", text: "#ad6800", border: "#ffd591" },
  resolved: { bg: "#f6ffed", text: "#389e0d", border: "#b7eb8f" },
  cancelled: { bg: "#f5f5f5", text: "#8c8c8c", border: "#d9d9d9" },
  expired: { bg: "#f5f5f5", text: "#8c8c8c", border: "#d9d9d9" },
  fake: { bg: "#FFF7ED", text: "#C2410C", border: "#FDBA74" },
};

const STATUS_LABELS = {
  active: "🔴 Active",
  responded: "🟡 Responded",
  resolved: "✅ Resolved",
  cancelled: "⚪ Cancelled",
  expired: "⏱️ Expired",
  fake: "🚫 Fake",
};

export default function PublicEmergencies() {
  const [emergencies, setEmergencies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [resolveModal, setResolveModal] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [detailModal, setDetailModal] = useState(null);
  const [stats, setStats] = useState({
    active: 0,
    responded: 0,
    resolved: 0,
    total: 0,
  });

  const [fakeModal, setFakeModal] = useState(null);
  const [fakeNote, setFakeNote] = useState("");
  const [blockPassenger, setBlockPassenger] = useState(false);
  const [fakeLoading, setFakeLoading] = useState(false);
  const [fakeHistoryModal, setFakeHistoryModal] = useState(null);
  const [fakeHistory, setFakeHistory] = useState(null);
  const [fakeHistoryLoading, setFakeHistoryLoading] = useState(false);

  // ✅ Voice message playback (table mini-player)
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [voiceAudio, setVoiceAudio] = useState(null);

  const limit = 10;
  const totalPages = Math.ceil(total / limit);

  // ═══════════════════════════════════════════════════════════════════
  // 📊 Get admin info helper
  // ═══════════════════════════════════════════════════════════════════
  const getAdminInfo = () => {
    const admin = localStorage.getItem("admin");
    const adminData = admin ? JSON.parse(admin) : null;
    return {
      name: adminData?.name || "Admin",
      email: adminData?.email || "",
      roleId: adminData?.roleId || "",
    };
  };

  // ─── Fetch ───────────────────────────────────
  const fetchEmergencies = useCallback(async () => {
    setLoading(true);
    const res = await getAllPublicSOS({
      n_limit: limit,
      n_page: page,
      c_search_term: search,
      c_status: statusFilter !== "all" ? statusFilter : "",
    });

    if (res.success) {
      setEmergencies(res.emergencies || []);
      setTotal(res.pagination?.totalCount || res.total || 0);
      if (res.stats) setStats(res.stats);
    }
    setLoading(false);
  }, [page, search, statusFilter]);

  // ═══════════════════════════════════════════════════════════════════
  // ✅ CONFIRM RESOLVE — API + Socket emit to notify ALL passengers
  // ═══════════════════════════════════════════════════════════════════
  const confirmResolve = async () => {
    if (!resolveModal) return;
    setResolving(resolveModal._id);

    const adminInfo = getAdminInfo();

    const res = await resolvePublicSOS(resolveModal._id, {
      resolvedBy: adminInfo.name,
      resolutionNote: resolveNote,
    });

    if (res.success) {
      const notifiedCount =
        (resolveModal.notifiedPassengers?.length || 0) +
        (resolveModal.notifiedDrivers?.length || 0);
      toast.success(`✅ SOS resolved! ${notifiedCount} nearby users notified.`);

      // ════════════════════════════════════════════════════════════════
      // ✅ EMIT sos-resolved via socket — This notifies ALL passengers
      //    passengerSocket.js handles:
      //    1. SOS sender → "sos-status-update" (shows resolved card)
      //    2. ALL notified passengers → "sos-status-update" (shows resolved banner)
      //    3. ALL notified drivers → "sos-status-update"
      //    4. Offline users → FCM push notification
      // ════════════════════════════════════════════════════════════════
      socket.emit("sos-resolved", {
        sosId: resolveModal.sosId || resolveModal._id,
        resolvedBy: adminInfo.roleId || adminInfo.email,
        resolvedByName: adminInfo.name,
        isReal: true,
        note: resolveNote || "",
      });

      console.log(
        `📡 sos-resolved emitted for ${resolveModal.sosId || resolveModal._id}`,
      );

      setResolveModal(null);
      setResolveNote("");
      fetchEmergencies();
    } else {
      toast.error(res.error || "Failed to resolve");
    }
    setResolving(null);
  };

  // ═══════════════════════════════════════════════════════════════════
  // ✅ CONFIRM FAKE — API + Socket emit to notify ALL passengers
  // ═══════════════════════════════════════════════════════════════════
  const confirmFake = async () => {
    if (!fakeModal) return;
    setFakeLoading(true);

    const adminInfo = getAdminInfo();

    const res = await markPublicSOSAsFake(fakeModal._id, {
      markedBy: adminInfo.name,
      fakeNote,
      blockPassenger,
    });

    if (res.success) {
      const warningMsg =
        res.warningLevel === "first_warning"
          ? "⚠️ 1st warning given to passenger"
          : res.warningLevel === "final_warning"
            ? "🔴 FINAL WARNING — next fake = BLOCK"
            : res.warningLevel === "blocked"
              ? "🚫 PASSENGER BLOCKED (3+ fake SOS)"
              : "";

      toast.success(`Marked as fake SOS. ${warningMsg}`);

      // ════════════════════════════════════════════════════════════════
      // ✅ EMIT sos-resolved with isReal=false — Notifies as "False Alarm"
      // ════════════════════════════════════════════════════════════════
      socket.emit("sos-resolved", {
        sosId: fakeModal.sosId || fakeModal._id,
        resolvedBy: adminInfo.roleId || adminInfo.email,
        resolvedByName: adminInfo.name,
        isReal: false,
        note: fakeNote || "Marked as false alarm by admin",
      });

      console.log(
        `📡 sos-resolved (fake) emitted for ${fakeModal.sosId || fakeModal._id}`,
      );

      setFakeModal(null);
      setFakeNote("");
      setBlockPassenger(false);
      fetchEmergencies();
    } else {
      toast.error(res.error || "Failed");
    }

    setFakeLoading(false);
  };

  // ✅ 8. FAKE HISTORY FUNCTION
  const openFakeHistory = async (passengerId) => {
    setFakeHistoryLoading(true);
    setFakeHistoryModal(true);

    const res = await getPassengerFakeHistory(passengerId);
    if (res.success) {
      setFakeHistory(res);
    }
    setFakeHistoryLoading(false);
  };

  useEffect(() => {
    fetchEmergencies();
  }, [fetchEmergencies]);

  // ─── Socket listeners ───────────────────────────────────
  useEffect(() => {
    const handlePublicSOS = (data) => {
      console.log("🚨 Public SOS received — refreshing...");
      fetchEmergencies();
    };

    const handleSOSCancelled = (data) => {
      console.log("ℹ️ Public SOS cancelled:", data.sosId);
      fetchEmergencies();
    };

    const handleSOSResponse = (data) => {
      console.log("🆘 SOS Response received:", data);
      fetchEmergencies();
    };

    // ✅ Listen for sos-resolved-admin (confirmation that resolve was processed)
    const handleSOSResolvedAdmin = (data) => {
      console.log("✅ SOS resolved confirmation:", data.sosId);
      fetchEmergencies();
    };

    socket.on("public-sos-alert", handlePublicSOS);
    socket.on("public-sos-cancelled", handleSOSCancelled);
    socket.on("sos-response-received", handleSOSResponse);
    socket.on("sos-resolved-admin", handleSOSResolvedAdmin);

    return () => {
      socket.off("public-sos-alert", handlePublicSOS);
      socket.off("public-sos-cancelled", handleSOSCancelled);
      socket.off("sos-response-received", handleSOSResponse);
      socket.off("sos-resolved-admin", handleSOSResolvedAdmin);
    };
  }, [fetchEmergencies]);

  // ─── Search ───────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  // ─── Resolve ──────────────────────────────────
  const openResolve = (emergency) => {
    setResolveModal(emergency);
    setResolveNote("");
  };

  // ─── Voice message play/pause (table) ─────────
  const toggleVoicePlay = (emergency) => {
    // Same one clicked again → pause & stop
    if (playingVoiceId === emergency._id) {
      voiceAudio?.pause();
      setPlayingVoiceId(null);
      setVoiceAudio(null);
      return;
    }

    // A different one is already playing → stop it first
    if (voiceAudio) {
      voiceAudio.pause();
    }

    const audio = new Audio(emergency.voiceMessage);
    audio.onended = () => {
      setPlayingVoiceId(null);
      setVoiceAudio(null);
    };
    audio.onerror = () => {
      toast.error("⚠️ Could not play voice message");
      setPlayingVoiceId(null);
      setVoiceAudio(null);
    };

    audio.play();
    setVoiceAudio(audio);
    setPlayingVoiceId(emergency._id);
  };

  // Stop playback if the component unmounts
  useEffect(() => {
    return () => {
      voiceAudio?.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceAudio]);

  // ─── Time format ──────────────────────────────
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="emg-page">
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN", "HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />

      {/* ─── Header ─── */}
      <div className="emg-header">
        <div className="emg-title">
          <span className="emg-title-icon">🆘</span>
          <div>
            <h1>Public SOS Alerts</h1>
            <p>{total} total alerts • Non-ride emergencies</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="emg-stats-row">
          <div className="emg-stat-card emg-stat-red">
            <span className="emg-stat-num">{stats.active || 0}</span>
            <span className="emg-stat-label">Active</span>
          </div>
          <div className="emg-stat-card emg-stat-yellow">
            <span className="emg-stat-num">{stats.responded || 0}</span>
            <span className="emg-stat-label">Responded</span>
          </div>
          <div className="emg-stat-card emg-stat-green">
            <span className="emg-stat-num">{stats.resolved || 0}</span>
            <span className="emg-stat-label">Resolved</span>
          </div>
          <div className="emg-stat-card emg-stat-orange">
            <span className="emg-stat-num">{stats.fake || 0}</span>
            <span className="emg-stat-label">Fake</span>
          </div>
        </div>
      </div>

      {/* ─── Tabs ─── */}
      <div className="emg-tabs">
        {["all", "active", "responded", "resolved", "cancelled", "fake"].map(
          (s) => (
            <button
              key={s}
              className={`emg-tab ${statusFilter === s ? "active" : ""} emg-tab--${s}`}
              onClick={() => {
                setStatusFilter(s);
                setPage(1);
              }}
            >
              {STATUS_LABELS[s] || "All"}
            </button>
          ),
        )}
      </div>

      {/* ─── Search ─── */}
      <form className="emg-search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Search by name, mobile, reason..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="emg-search-input"
        />
        <button type="submit" className="emg-search-btn">
          Search
        </button>
        {search && (
          <button
            type="button"
            className="emg-clear-btn"
            onClick={() => {
              setSearch("");
              setSearchInput("");
              setPage(1);
            }}
          >
            ✕ Clear
          </button>
        )}
      </form>

      {/* ─── Table ─── */}
      {loading ? (
        <Loader />
      ) : emergencies.length === 0 ? (
        <div className="emg-empty">
          <span>🎉</span>
          <p>No public SOS alerts found</p>
        </div>
      ) : (
        <div className="emg-table-wrap">
          <table className="emg-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Passenger</th>
                <th>Reason</th>
                <th>Responders</th>
                <th>Location</th>
                <th>Time</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {emergencies.map((e, i) => {
                const sc = STATUS_COLORS[e.status] || STATUS_COLORS.active;
                return (
                  <tr key={e._id} className="emg-row">
                    <td className="emg-num">{(page - 1) * limit + i + 1}</td>

                    <td>
                      <div className="emg-passenger">
                        <span className="emg-pname">{e.passengerName}</span>
                        <span className="emg-pmobile">{e.passengerMobile}</span>
                        {e.source && (
                          <span className="emg-source-badge">{e.source}</span>
                        )}
                      </div>
                    </td>

                    <td>
                      {e.reason ? (
                        <div className="emg-reason-cell">
                          <span className="emg-reason-badge">
                            {REASON_EMOJI[e.reason] || "❗"} {e.reason}
                          </span>
                          {e.reasonNote && (
                            <span className="emg-reason-note">
                              "{e.reasonNote}"
                            </span>
                          )}
                          {e.voiceMessage && (
                            <button
                              type="button"
                              className="emg-voice-badge"
                              onClick={() => toggleVoicePlay(e)}
                              style={{ cursor: "pointer", border: "none" }}
                              title={
                                playingVoiceId === e._id
                                  ? "Pause voice message"
                                  : "Play voice message"
                              }
                            >
                              {playingVoiceId === e._id
                                ? "⏸️ Playing..."
                                : "🎤 Play Voice"}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="emg-na">—</span>
                      )}
                    </td>

                    {/* Responders */}
                    <td>
                      {e.responders?.length > 0 ? (
                        <div className="emg-responders">
                          {e.responders.slice(0, 2).map((r, ri) => (
                            <div key={ri} className="emg-responder-chip">
                              <span>🆘</span>
                              <span>{r.responderName}</span>
                              <span className="emg-resp-type">
                                {r.responseType === "coming_to_help"
                                  ? "🏃"
                                  : r.responseType === "called_emergency"
                                    ? "📞"
                                    : "📢"}
                              </span>
                            </div>
                          ))}
                          {e.responders.length > 2 && (
                            <span className="emg-more-resp">
                              +{e.responders.length - 2} more
                            </span>
                          )}
                          {/* ✅ Notified count badge */}
                          {(e.notifiedPassengers?.length > 0 ||
                            e.notifiedDrivers?.length > 0) && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#6B7280",
                                marginTop: 4,
                                display: "flex",
                                gap: 4,
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  background: "#EFF6FF",
                                  color: "#1D4ED8",
                                  borderRadius: 8,
                                  padding: "1px 7px",
                                  fontWeight: 600,
                                }}
                              >
                                📢 {e.notifiedPassengers?.length || 0}P +{" "}
                                {e.notifiedDrivers?.length || 0}D notified
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="emg-na">No responses</span>
                          {(e.notifiedPassengers?.length > 0 ||
                            e.notifiedDrivers?.length > 0) && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#6B7280",
                                marginTop: 4,
                              }}
                            >
                              <span
                                style={{
                                  background: "#EFF6FF",
                                  color: "#1D4ED8",
                                  borderRadius: 8,
                                  padding: "1px 7px",
                                  fontWeight: 600,
                                }}
                              >
                                📢 {e.notifiedPassengers?.length || 0}P +{" "}
                                {e.notifiedDrivers?.length || 0}D notified
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    <td>
                      <a
                        className="emg-location-link"
                        href={`https://maps.google.com/?q=${e.location?.coordinates?.[1]},${e.location?.coordinates?.[0]}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        📍 View Map
                      </a>
                    </td>

                    <td className="emg-time">
                      <span className="emg-time-ago">
                        {timeAgo(e.createdAt)}
                      </span>
                      <br />
                      <span>{new Date(e.createdAt).toLocaleTimeString()}</span>
                    </td>

                    <td>
                      <span
                        className="emg-status-badge"
                        style={{
                          background: sc.bg,
                          color: sc.text,
                          border: `1px solid ${sc.border}`,
                        }}
                      >
                        {STATUS_LABELS[e.status] || e.status}
                      </span>
                      {e.resolvedBy && (
                        <div className="emg-resolved-by">by {e.resolvedBy}</div>
                      )}
                    </td>

                    <td>
                      <div className="emg-action-btns">
                        <button
                          className="emg-detail-btn"
                          onClick={() => setDetailModal(e)}
                        >
                          👁️ View
                        </button>
                        {(e.status === "active" ||
                          e.status === "responded") && (
                          <>
                            <button
                              className="emg-resolve-btn"
                              onClick={() => openResolve(e)}
                              disabled={resolving === e._id}
                            >
                              {resolving === e._id ? "..." : "✓ Resolve"}
                            </button>
                            <button
                              className="emg-fake-btn"
                              onClick={() => {
                                setFakeModal(e);
                                setFakeNote("");
                                setBlockPassenger(false);
                              }}
                            >
                              🚫 Fake
                            </button>
                          </>
                        )}

                        {e.status === "fake" && (
                          <div className="emg-fake-badge-row">
                            <span className="emg-fake-badge-sm">🚫 Fake</span>
                            <button
                              className="emg-history-btn"
                              onClick={() => openFakeHistory(e.passengerId)}
                            >
                              📋 History
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Pagination ─── */}
      {totalPages > 1 && (
        <div className="emg-pagination">
          <button
            className="emg-page-btn"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1,
            )
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === "..." ? (
                <span key={`dots-${i}`} className="emg-dots">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={`emg-page-btn ${page === p ? "active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ),
            )}
          <button
            className="emg-page-btn"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {/* ─── Detail Modal ─── */}
      {detailModal && (
        <div className="emg-modal-overlay" onClick={() => setDetailModal(null)}>
          <div
            className="emg-modal emg-modal-lg"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="emg-modal-header">
              <h2>🆘 Public SOS Details</h2>
              <button
                className="emg-modal-close"
                onClick={() => setDetailModal(null)}
              >
                ✕
              </button>
            </div>

            <div className="emg-modal-summary">
              <div className="emg-modal-row">
                <span>🆔 SOS ID</span>
                <strong>{detailModal.sosId || detailModal._id}</strong>
              </div>
              <div className="emg-modal-row">
                <span>👤 Passenger</span>
                <strong>{detailModal.passengerName}</strong>
              </div>
              <div className="emg-modal-row">
                <span>📞 Mobile</span>
                <strong>{detailModal.passengerMobile}</strong>
              </div>
              <div className="emg-modal-row">
                <span>⚡ Reason</span>
                <strong>
                  {REASON_EMOJI[detailModal.reason]} {detailModal.reason}
                  {detailModal.reasonNote
                    ? ` — "${detailModal.reasonNote}"`
                    : ""}
                </strong>
              </div>
              <div className="emg-modal-row">
                <span>📱 Source</span>
                <strong>{detailModal.source || "N/A"}</strong>
              </div>
              <div className="emg-modal-row">
                <span>📍 Location</span>
                <a
                  href={`https://maps.google.com/?q=${detailModal.location?.coordinates?.[1]},${detailModal.location?.coordinates?.[0]}`}
                  target="_blank"
                  rel="noreferrer"
                  className="emg-location-link"
                >
                  View on Map ↗
                </a>
              </div>
              <div className="emg-modal-row">
                <span>🕐 Time</span>
                <strong>
                  {new Date(detailModal.createdAt).toLocaleString()}
                </strong>
              </div>
              {detailModal.voiceMessage && (
                <div
                  className="emg-modal-row"
                  style={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 6,
                  }}
                >
                  <span>🎤 Voice Message</span>
                  <audio
                    controls
                    src={detailModal.voiceMessage}
                    style={{ width: "100%" }}
                  >
                    Your browser does not support audio playback.
                  </audio>
                </div>
              )}
            </div>

            {/* Responders List */}
            {detailModal.responders?.length > 0 && (
              <div className="emg-detail-section">
                <h3>🆘 Responders ({detailModal.responders.length})</h3>
                <div className="emg-responders-list">
                  {detailModal.responders.map((r, i) => (
                    <div key={i} className="emg-responder-detail">
                      <div className="emg-resp-info">
                        <strong>{r.responderName}</strong>
                        <span>{r.responderPhone || "N/A"}</span>
                      </div>
                      <div className="emg-resp-meta">
                        <span className="emg-resp-type-badge">
                          {r.responseType === "coming_to_help"
                            ? "🏃 Coming to help"
                            : r.responseType === "called_emergency"
                              ? "📞 Called 112"
                              : "📢 Notified others"}
                        </span>
                        <span className="emg-resp-time">
                          {new Date(r.respondedAt).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ✅ NEW — Notified Passengers List */}
            <div className="emg-detail-section">
              <h3>
                📢 Notified Nearby Users (
                {(detailModal.notifiedPassengers?.length || 0) +
                  (detailModal.notifiedDrivers?.length || 0)}
                )
              </h3>

              {/* Passenger count summary */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    border: "1px solid #BFDBFE",
                    borderRadius: 20,
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  👥 {detailModal.notifiedPassengers?.length || 0} Passengers
                </span>
                <span
                  style={{
                    background: "#F0FDF4",
                    color: "#15803D",
                    border: "1px solid #86EFAC",
                    borderRadius: 20,
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  🛺 {detailModal.notifiedDrivers?.length || 0} Drivers
                </span>
              </div>

              {/* Passenger IDs list */}
              {detailModal.notifiedPassengers?.length > 0 ? (
                <div
                  style={{
                    maxHeight: 200,
                    overflowY: "auto",
                    border: "1px solid #E5E7EB",
                    borderRadius: 10,
                    background: "#FAFAFA",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: "#F3F4F6",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        <th
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            color: "#6B7280",
                            fontWeight: 600,
                          }}
                        >
                          #
                        </th>
                        <th
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            color: "#6B7280",
                            fontWeight: 600,
                          }}
                        >
                          Passenger ID
                        </th>
                        <th
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            color: "#6B7280",
                            fontWeight: 600,
                          }}
                        >
                          Responded?
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailModal.notifiedPassengers.map((pid, i) => {
                        const isResponder = detailModal.responders?.some(
                          (r) => r.responderId === pid,
                        );
                        return (
                          <tr
                            key={pid}
                            style={{
                              borderBottom: "1px solid #F3F4F6",
                              background: isResponder
                                ? "#F0FDF4"
                                : "transparent",
                            }}
                          >
                            <td
                              style={{ padding: "7px 12px", color: "#9CA3AF" }}
                            >
                              {i + 1}
                            </td>
                            <td
                              style={{
                                padding: "7px 12px",
                                fontFamily: "monospace",
                                color: "#374151",
                                fontWeight: 500,
                              }}
                            >
                              {pid}
                            </td>
                            <td style={{ padding: "7px 12px" }}>
                              {isResponder ? (
                                <span
                                  style={{
                                    background: "#D1FAE5",
                                    color: "#065F46",
                                    borderRadius: 10,
                                    padding: "2px 8px",
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                >
                                  ✅ Responded
                                </span>
                              ) : (
                                <span
                                  style={{
                                    background: "#F3F4F6",
                                    color: "#9CA3AF",
                                    borderRadius: 10,
                                    padding: "2px 8px",
                                    fontSize: 11,
                                  }}
                                >
                                  — Notified
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  style={{ color: "#9CA3AF", fontSize: 13, padding: "10px 0" }}
                >
                  No nearby passengers were notified.
                </div>
              )}
            </div>

            {/* Resolution info */}
            {detailModal.status === "resolved" && (
              <div className="emg-detail-section">
                <h3>✅ Resolution</h3>
                <div className="emg-modal-row">
                  <span>Resolved by</span>
                  <strong>{detailModal.resolvedBy}</strong>
                </div>
                {detailModal.resolutionNote && (
                  <div className="emg-modal-row">
                    <span>Note</span>
                    <strong>{detailModal.resolutionNote}</strong>
                  </div>
                )}
                <div className="emg-modal-row">
                  <span>Resolved at</span>
                  <strong>
                    {new Date(detailModal.resolvedAt).toLocaleString()}
                  </strong>
                </div>
                <div className="emg-modal-row">
                  <span>📢 Notified</span>
                  <strong style={{ color: "#15803D" }}>
                    {(detailModal.notifiedPassengers?.length || 0) +
                      (detailModal.notifiedDrivers?.length || 0)}{" "}
                    users informed about resolution
                  </strong>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Resolve Modal ─── */}
      {resolveModal && (
        <div
          className="emg-modal-overlay"
          onClick={() => setResolveModal(null)}
        >
          <div className="emg-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="emg-modal-header">
              <h2>✅ Resolve Public SOS</h2>
              <button
                className="emg-modal-close"
                onClick={() => setResolveModal(null)}
              >
                ✕
              </button>
            </div>

            <div className="emg-modal-summary">
              <div className="emg-modal-row">
                <span>👤 Passenger</span>
                <strong>{resolveModal.passengerName}</strong>
              </div>
              <div className="emg-modal-row">
                <span>📞 Mobile</span>
                <strong>{resolveModal.passengerMobile}</strong>
              </div>
              {resolveModal.reason && (
                <div className="emg-modal-row">
                  <span>⚡ Reason</span>
                  <strong>
                    {REASON_EMOJI[resolveModal.reason]} {resolveModal.reason}
                  </strong>
                </div>
              )}
              {resolveModal.responders?.length > 0 && (
                <div className="emg-modal-row">
                  <span>🆘 Responders</span>
                  <strong>
                    {resolveModal.responders.length} people responded
                  </strong>
                </div>
              )}
              <div className="emg-modal-row">
                <span>📢 Will Notify</span>
                <strong style={{ color: "#1677ff" }}>
                  {(resolveModal.notifiedPassengers?.length || 0) +
                    (resolveModal.notifiedDrivers?.length || 0)}{" "}
                  nearby users
                  <span
                    style={{
                      fontWeight: 400,
                      color: "#6B7280",
                      fontSize: 12,
                      marginLeft: 6,
                    }}
                  >
                    ({resolveModal.notifiedPassengers?.length || 0} passengers +{" "}
                    {resolveModal.notifiedDrivers?.length || 0} drivers)
                  </span>
                </strong>
              </div>
            </div>

            {/* ✅ Notify Info Banner */}
            <div
              style={{
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 14,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <span style={{ fontSize: 20 }}>📲</span>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    color: "#1D4ED8",
                    fontSize: 13,
                    marginBottom: 3,
                  }}
                >
                  Resolve பண்ணினால் என்ன ஆகும்?
                </div>
                <div
                  style={{ color: "#374151", fontSize: 12, lineHeight: 1.6 }}
                >
                  • SOS sender → <strong>"✅ Help Arrived!"</strong>{" "}
                  notification கிடைக்கும்
                  <br />• {resolveModal.notifiedPassengers?.length || 0} nearby
                  passengers → <strong>"SOS Resolved"</strong> alert கிடைக்கும்
                  <br />• {resolveModal.notifiedDrivers?.length || 0} nearby
                  drivers → notified ஆவார்கள்
                  <br />• Offline users → FCM push notification பெறுவார்கள்
                </div>
              </div>
            </div>

            <div className="emg-modal-note-section">
              <label>
                Resolution Note <span>(optional)</span>
              </label>
              <textarea
                className="emg-modal-textarea"
                placeholder="e.g. Police dispatched, passenger safe, false alarm..."
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                rows={3}
              />
            </div>

            <div className="emg-modal-actions">
              <button
                className="emg-modal-cancel"
                onClick={() => setResolveModal(null)}
              >
                Cancel
              </button>
              <button
                className="emg-modal-confirm"
                onClick={confirmResolve}
                disabled={resolving === resolveModal._id}
              >
                {resolving === resolveModal._id
                  ? "🔄 Notifying passengers..."
                  : "✓ Resolve & Notify All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {fakeModal && (
        <div className="emg-modal-overlay" onClick={() => setFakeModal(null)}>
          <div className="emg-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="emg-modal-header">
              <h2>🚫 Mark as Fake SOS</h2>
              <button
                className="emg-modal-close"
                onClick={() => setFakeModal(null)}
              >
                ✕
              </button>
            </div>

            {/* Passenger Info */}
            <div className="emg-modal-summary">
              <div className="emg-modal-row">
                <span>👤 Passenger</span>
                <strong>{fakeModal.passengerName}</strong>
              </div>
              <div className="emg-modal-row">
                <span>📞 Mobile</span>
                <strong>{fakeModal.passengerMobile}</strong>
              </div>
              <div className="emg-modal-row">
                <span>🆔 Passenger ID</span>
                <strong>{fakeModal.passengerId}</strong>
              </div>
              {fakeModal.reason && (
                <div className="emg-modal-row">
                  <span>⚡ Reported Reason</span>
                  <strong>{fakeModal.reason}</strong>
                </div>
              )}
            </div>

            {/* Warning Banner */}
            <div className="emg-fake-warning-banner">
              <span>⚠️</span>
              <div>
                <strong>Warning System:</strong>
                <p>
                  1st fake → Warning • 2nd fake → Final Warning • 3rd fake →
                  Auto Block
                </p>
              </div>
            </div>

            {/* Fake Reason Note */}
            <div className="emg-modal-note-section">
              <label>
                Why is this fake? <span>(optional)</span>
              </label>
              <textarea
                className="emg-modal-textarea"
                placeholder="e.g. Testing the system, prank call, no real emergency..."
                value={fakeNote}
                onChange={(e) => setFakeNote(e.target.value)}
                rows={3}
              />
            </div>

            {/* Block Passenger Toggle */}
            <div className="emg-fake-block-row">
              <label className="emg-fake-toggle">
                <input
                  type="checkbox"
                  checked={blockPassenger}
                  onChange={(e) => setBlockPassenger(e.target.checked)}
                />
                <span className="emg-fake-toggle-slider" />
              </label>
              <div>
                <strong>Block this passenger immediately</strong>
                <p>Passenger won't be able to use the app</p>
              </div>
            </div>

            {/* Actions */}
            <div className="emg-modal-actions">
              <button
                className="emg-modal-cancel"
                onClick={() => setFakeModal(null)}
              >
                Cancel
              </button>
              <button
                className="emg-modal-confirm emg-modal-confirm-fake"
                onClick={confirmFake}
                disabled={fakeLoading}
              >
                {fakeLoading ? "Processing..." : "🚫 Confirm Fake & Warn"}
              </button>
            </div>
          </div>
        </div>
      )}

      {fakeHistoryModal && (
        <div
          className="emg-modal-overlay"
          onClick={() => {
            setFakeHistoryModal(null);
            setFakeHistory(null);
          }}
        >
          <div
            className="emg-modal emg-modal-lg"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="emg-modal-header">
              <h2>📋 Fake SOS History</h2>
              <button
                className="emg-modal-close"
                onClick={() => {
                  setFakeHistoryModal(null);
                  setFakeHistory(null);
                }}
              >
                ✕
              </button>
            </div>

            {fakeHistoryLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <p>Loading...</p>
              </div>
            ) : fakeHistory ? (
              <>
                {/* Passenger Status */}
                <div className="emg-fake-history-header">
                  <div className="emg-fake-history-passenger">
                    <strong>{fakeHistory.passenger?.name || "Unknown"}</strong>
                    <span>{fakeHistory.passenger?.phone}</span>
                    <span>{fakeHistory.passenger?.passengerId}</span>
                  </div>
                  <div className="emg-fake-history-status">
                    <div
                      className={`emg-fake-status-badge ${
                        fakeHistory.isBlocked
                          ? "emg-fake-blocked"
                          : fakeHistory.warningLevel === "final_warning"
                            ? "emg-fake-final"
                            : fakeHistory.warningLevel === "first_warning"
                              ? "emg-fake-first"
                              : "emg-fake-clean"
                      }`}
                    >
                      {fakeHistory.isBlocked
                        ? "🚫 BLOCKED"
                        : fakeHistory.warningLevel === "final_warning"
                          ? "🔴 Final Warning"
                          : fakeHistory.warningLevel === "first_warning"
                            ? "🟡 1st Warning"
                            : "✅ Clean"}
                    </div>
                    <span className="emg-fake-count">
                      {fakeHistory.totalFake} fake SOS total
                    </span>
                  </div>
                </div>

                {/* Fake SOS List */}
                {fakeHistory.fakeAlerts?.length > 0 ? (
                  <div className="emg-fake-history-list">
                    {fakeHistory.fakeAlerts.map((fa, i) => (
                      <div key={fa._id} className="emg-fake-history-item">
                        <div className="emg-fake-history-num">{i + 1}</div>
                        <div className="emg-fake-history-info">
                          <div className="emg-fake-history-reason">
                            {fa.reason || "No reason"} —{" "}
                            {fa.fakeNote || "No note"}
                          </div>
                          <div className="emg-fake-history-meta">
                            📅{" "}
                            {new Date(
                              fa.fakeMarkedAt || fa.createdAt,
                            ).toLocaleString()}
                            &nbsp;• Marked by {fa.fakeMarkedBy || "Admin"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: 30,
                      color: "#9CA3AF",
                    }}
                  >
                    No fake SOS records found
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
